import { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useFormikContext } from 'formik';
import { useHistory, generatePath } from 'react-router-dom';
import { useTelemetry, usePrevious, usePersonalLoan } from 'shared/hooks';
import {
  Button as ButtonBase,
  COLORS,
  InputError,
  SPACINGS,
  useMediaQuery,
} from '@figure/figure-frontlib-gui';
import { Card } from 'shared/components/Card';
import { isUuid } from 'shared/utils';
import { reduceFormElementsToKeyOrValue } from './utils';
import { DynamicFormElement } from './DynamicFormElement';

const Button = styled(ButtonBase)`
  margin-bottom: 16px;
`;
const FormElementsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACINGS.L}px;
`;
const ButtonContainer = styled.div`
  ${({ isMobileLayout }) =>
    isMobileLayout
      ? css`
          position: sticky;
          bottom: 0;
          width: 100%;
          background: white;
          padding: ${SPACINGS.M}px 0;
          display: flex;
          flex-wrap: wrap;
          z-index: 100;
          gap: ${SPACINGS.M}px;
          justify-content: center;

          button {
            margin: 0 ${SPACINGS.X4L}px;
            flex: 1 1 100%;
            min-width: inherit;
          }
          &::before {
            content: '';
            position: absolute;
            top: 0;
            /* Padding of cards is 32px; */
            left: -32px;
            width: calc(100% + 64px);
            border-top: solid ${COLORS.NEUTRAL_250} 1px;
          }
        `
      : css`
          padding-top: ${SPACINGS.M}px;
          display: flex;
          gap: ${SPACINGS.L}px;
          justify-content: flex-end;
          button {
            margin: 0;
          }
        `}
`;

const useAutoAdvanceToNextStep = ({
  formElements,
  steps,
  currentStepIndex,
  handleNavigateToNextStep,
}) => {
  const { values } = useFormikContext();
  const currentStepFields = formElements.filter(({ name }) => !!name);
  const areAllFieldsButLastForStepFilledOut = currentStepFields
    .slice(0, -1)
    .every(({ name }) => !!values[name]);
  const isLastFieldForStepRadio = ['radio', 'cardSelect'].includes(
    currentStepFields[currentStepFields.length - 1].formElementComponentType
  );
  const isNotLastStep = steps.length - 1 !== currentStepIndex;
  const shouldAutoNavigateToNextStepOnChange =
    areAllFieldsButLastForStepFilledOut && isLastFieldForStepRadio && isNotLastStep;
  const lastFieldValue = values[currentStepFields[currentStepFields.length - 1].name];
  const previousLastFieldValue = usePrevious(lastFieldValue, lastFieldValue);
  useEffect(() => {
    if (shouldAutoNavigateToNextStepOnChange && lastFieldValue !== previousLastFieldValue) {
      handleNavigateToNextStep();
    }
  }, [
    handleNavigateToNextStep,
    lastFieldValue,
    previousLastFieldValue,
    shouldAutoNavigateToNextStepOnChange,
  ]);
};

const usePageLoadTelemetry = ({ currentStepIndex }) => {
  const { telemetry } = useTelemetry();
  useEffect(() => {
    telemetry(`plBasicInfo${currentStepIndex + 1}Viewed`);
  }, [currentStepIndex, telemetry]);
};

export const Step = ({
  steps,
  step: { path, header, nextButtonText, formElements },
  currentStepIndex,
  match,
  appUuid,
  formError,
}) => {
  const history = useHistory();
  const { telemetry } = useTelemetry();
  const { validateForm, setTouched, isValidating, isSubmitting } = useFormikContext();
  const { createPersonalLoanApplication } = usePersonalLoan(false);
  const mediaQueryMatches = useMediaQuery();
  const isMobileLayout = !mediaQueryMatches.MD;

  usePageLoadTelemetry({ currentStepIndex });

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex + 1 === steps.length;
  const previousStep = isFirstStep ? null : steps[currentStepIndex - 1];
  const nextStep = isLastStep ? null : steps[currentStepIndex + 1];

  const handleNavigateToPreviousStep = () => {
    if (!isFirstStep) {
      history.push(generatePath(`${match.path}/${previousStep.path}`, { uuid: appUuid }));
      window.scrollTo(0, 0);
      telemetry(`plBasicInfo${currentStepIndex + 1}Back`);
    }
  };

  const handleNavigateToNextStep = useCallback(async () => {
    const errors = await validateForm();
    const hasErrorOnCurrentStep = formElements.some(({ name }) => !!errors[name]);
    if (hasErrorOnCurrentStep) {
      setTouched(
        reduceFormElementsToKeyOrValue({
          items: formElements,
          value: true,
          shouldHandleAddress: true,
        })
      );
    } else if (!isLastStep) {
      telemetry(`plBasicInfo${currentStepIndex + 1}ContinueButtonClicked`);
      let currentUuid = appUuid;
      if (!isUuid(appUuid)) {
        const { uuid } = await createPersonalLoanApplication();
        currentUuid = uuid;
      }
      history.push(generatePath(`${match.path}/${nextStep?.path}`, { uuid: currentUuid }));
      window.scrollTo(0, 0);
    }
  }, [
    appUuid,
    createPersonalLoanApplication,
    currentStepIndex,
    formElements,
    history,
    isLastStep,
    match.path,
    nextStep?.path,
    setTouched,
    telemetry,
    validateForm,
  ]);

  const actionButtons = [
    ...(!isFirstStep
      ? [
          <Button key="back" variant="secondary" onClick={handleNavigateToPreviousStep}>
            Back
          </Button>,
        ]
      : []),
    <Button
      key="next"
      type={isLastStep ? 'submit' : 'button'}
      {...(!isLastStep ? { onClick: handleNavigateToNextStep } : {})}
      disabled={isValidating || isSubmitting}
    >
      {nextButtonText}
    </Button>,
  ];

  // Decides if it should auto navigate to next step when radio input or card select values are changed.
  useAutoAdvanceToNextStep({
    formElements,
    steps,
    currentStepIndex,
    handleNavigateToNextStep,
  });

  return (
    <Card
      headerCopy={header}
      pageTitle="Personal Loan"
      progress={{ currentStep: currentStepIndex + 1, totalSteps: steps.length }}
      customMaxWidth="840px"
    >
      <FormElementsContainer>
        {formElements.map((formElement, formElementIndex) => (
          <DynamicFormElement
            key={formElement.name || formElementIndex}
            formElement={formElement}
            formElementIndex={formElementIndex}
          />
        ))}
      </FormElementsContainer>
      {!!formError && <InputError errorText={formError} />}
      <ButtonContainer isMobileLayout={isMobileLayout}>
        {isMobileLayout ? [...actionButtons].reverse() : actionButtons}
      </ButtonContainer>
    </Card>
  );
};
Step.propTypes = {
  steps: PropTypes.array.isRequired,
  step: PropTypes.shape({
    path: PropTypes.string,
    header: PropTypes.node,
    nextButtonText: PropTypes.node,
    nextButtonHelperText: PropTypes.node,
    formElements: PropTypes.array,
  }).isRequired,
  currentStepIndex: PropTypes.number.isRequired,
  match: PropTypes.shape({ path: PropTypes.string }).isRequired,
  appUuid: PropTypes.string.isRequired,
  formError: PropTypes.string,
};
Step.defaultProps = {
  formError: '',
};
